import React, { useState, useEffect } from 'react';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { BuildingOfficeIcon, EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { validatePhoneNumber, validateEmail, validateAddress } from '../utils/validation';
import {ToastContainer, toast} from 'react-toastify';
import { Autocomplete } from '@react-google-maps/api';

export default function Profile() {
  const { user } = useAuth();
  const { logEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessProfile, setBusinessProfile] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    tax_number: '',
    logo_url: null,
  });
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [autocomplete, setAutocomplete] = useState(null);


  useEffect(() => {
    if (user) {
      fetchBusinessProfile();
    }
  }, [user]);

  useEffect(() => {
    logEvent('Profile', 'View Profile', 'Page Load');
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('business_profile')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      if (data) {
        setBusinessProfile(data);
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
      setError('Failed to load business profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Math.random()}.${fileExt}`;

      // Upload logo to storage
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      setBusinessProfile(prev => ({
        ...prev,
        logo_url: publicUrl
      }));
      logEvent('Profile', 'Upload Avatar', 'Avatar Upload Attempt');
      logEvent('Profile', 'Upload Avatar Success', 'Avatar Updated');
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Failed to upload logo');
      logEvent('Profile', 'Upload Avatar Error', error.message);
    }
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setBusinessProfile(prev => ({
      ...prev,
      business_phone: value
    }));
    setErrors(prev => ({ ...prev, business_phone: null }));
  };

  const handleAddressChange = (e) => {
    const { value } = e.target;
    setBusinessProfile((prev) => ({
      ...prev,
      business_address: value,
    }));
    setErrors((prev) => ({ ...prev, business_address: null }));
  };

  const onLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) {
        window.alert("No details available for input: '" + place.name + "'");
        return;
      }

      const formattedAddress = place.formatted_address;
      // Directly update the address, no need for client-side validation
      setBusinessProfile((prev) => ({
        ...prev,
        business_address: formattedAddress,
      }));
      setErrors((prev) => ({ ...prev, business_address: '' }));
    } else {
      console.log('Autocomplete is not loaded yet!');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const isValid = await validateForm();
    if (!isValid) {
      setSaving(false);
      return;
    }

    try {
      const phoneValidation = validatePhoneNumber(businessProfile.business_phone);
      const formattedPhone = phoneValidation.isValid ? phoneValidation.formattedNumber : businessProfile.business_phone;

      const { error } = await supabase
        .from('business_profile')
        .upsert({
          user_id: user.id,
          business_phone: formattedPhone,
          business_email: businessProfile.business_email,
          business_name: businessProfile.business_name,
          business_address: businessProfile.business_address,
          tax_number: businessProfile.tax_number,
          logo_url: businessProfile.logo_url,
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          // Instead of throwing error, update the existing record
          const { error: updateError } = await supabase
            .from('business_profile')
            .update({
              business_phone: formattedPhone,
              business_email: businessProfile.business_email,
              business_name: businessProfile.business_name,
              business_address: businessProfile.business_address,
              tax_number: businessProfile.tax_number,
              logo_url: businessProfile.logo_url,
            })
            .eq('user_id', user.id);

          if (updateError) throw updateError;
        } else {
          throw error;
        }
      }

      toast.success('Profile updated successfully');
      logEvent('Profile', 'Update Profile Success', 'Profile Updated');
    } catch (error) {
      console.error('Error saving business profile:', error);
      setError('Failed to save business profile');
      logEvent('Profile', 'Update Profile Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    // Client name validation
    if (!businessProfile.business_name.trim()) {
      newErrors.business_name = 'Business name is required';
    }
    // Email validation
    if (businessProfile.business_email) {
      const emailValidation = validateEmail(businessProfile.business_email);
      if (!emailValidation.isValid) {
        newErrors.business_email = emailValidation.error;
      }
    }
    // Phone validation
    if (businessProfile.business_phone) {
      const phoneValidation = validatePhoneNumber(businessProfile.business_phone);
      if (!phoneValidation.isValid) {
        newErrors.business_phone = phoneValidation.error;
      }
    }
    // Address validation
    if (businessProfile.business_address) {
      const addressValidation = await validateAddress(businessProfile.business_address);
      if (!addressValidation.isValid) {
        newErrors.business_address = addressValidation.error;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Business Profile</h1>
        <p style={{ color: THEME.subtext }}>Manage your business information and branding</p>
      </div>
      

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          {/* Logo Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
              Business Logo
            </label>
            <div className="flex items-center space-x-4">
              {businessProfile.logo_url ? (
                <img
                  src={businessProfile.logo_url}
                  alt="Business Logo"
                  className="w-20 h-20 rounded-lg object-cover"
                />
              ) : (
                <div 
                  className="w-20 h-20 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${THEME.primary}15` }}
                >
                  <BuildingOfficeIcon className="w-8 h-8" style={{ color: THEME.primary }} />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="px-4 py-2 text-sm font-medium rounded-lg cursor-pointer"
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                Change Logo
              </label>
            </div>
          </div>

          {/* Business Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Business Name
              </label>
              <input
                type="text"
                name="business_name"
                value={businessProfile.business_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
              {errors.business_name && (
                <p className="text-sm mt-1" style={{ color: THEME.error }}>
                  {errors.business_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Business Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="business_email"
                  value={businessProfile.business_email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
                {errors.business_email && (
                  <p className="text-sm mt-1" style={{ color: THEME.error }}>
                    {errors.business_email}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="business_phone"
                  value={businessProfile.business_phone}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number (e.g. +61412345678)"
                  className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                  style={{ 
                    borderColor: errors.business_phone ? THEME.error : undefined 
                  }}
                />
                {errors.business_phone && (
                  <p className="text-sm mt-1" style={{ color: THEME.error }}>
                    {errors.business_phone}
                  </p>
                )}
              </div>
            </div>
            <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Business Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPinIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                    <input
                      type="text"
                      name="business_address"
                      value={businessProfile.business_address}
                      onChange={handleAddressChange}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                      style={{
                        borderColor: errors.business_address
                          ? THEME.error
                          : undefined,
                      }}
                    />
                  </Autocomplete>
                  {errors.business_address && (
                    <p className="text-sm mt-1" style={{ color: THEME.error }}>
                      {errors.business_address}
                    </p>
                  )}
                </div>
              </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Tax Number
              </label>
              <input
                type="text"
                name="tax_number"
                value={businessProfile.tax_number}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="e.g., VAT or GST number"
              />
              {errors.tax_number && (
                <p className="text-sm mt-1" style={{ color: THEME.error }}>
                  {errors.tax_number}
                </p>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div 
            className="rounded-lg p-4 text-sm"
            style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
            style={{ 
              backgroundColor: THEME.primary,
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      <ToastContainer />
    </div>
  );
} 